import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { AppLocale } from "@/i18n/constants";

/**
 * Published prices from "Servicios Lucentia España 2026".
 * The owner confirmed (2026-09-27) that the listed prices include IVA. Every
 * price note, the /precios page, the terms and the Offer schema follow this flag.
 */
export const PRICES_INCLUDE_VAT = true;

export type PackId = "bienvenida" | "esencia" | "crecimiento" | "evolucion";

export type PricingPack = {
  id: PackId;
  name: string;
  goal: string;
  price: number;
  cadence: "once" | "month";
  idealFor: string;
  includes: readonly string[];
  outcomes: readonly string[];
  featured?: boolean;
  note?: string;
};

export type RateItem = {
  service: string;
  detail: string;
  min: number;
  max?: number;
};

/** A standalone service with its own published price. */
export type ServicePrice = {
  id: "asesoria" | "web" | "whatsapp" | "google";
  title: string;
  desc: string;
  price: number;
  /** Shows the price as a starting point ("desde"). */
  from?: boolean;
  cadence: "once" | "month";
  /** Optional recurring extra shown under the price. */
  extra?: string;
  href?: string;
};

export type AddOn = {
  title: string;
  desc: string;
  by: string;
};

export type PricingContent = {
  page: { title: string; lead: string; plansTitle: string; tableService: string; tablePrice: string };
  currency: "EUR";
  taxNote: string;
  perMonth: string;
  oneOff: string;
  from: string;
  featuredLabel: string;
  packs: readonly PricingPack[];
  networkNote: string;
  firstStep: { title: string; body: string };
  custom: { title: string; body: string; factors: readonly string[]; note: string };
  services: { title: string; intro: string; cta: string; items: readonly ServicePrice[] };
  rateCard: { title: string; intro: string; note: string; items: readonly RateItem[] };
  addOns: { title: string; intro: string; items: readonly AddOn[] };
  notIncluded: { title: string; items: readonly string[] };
  conditions: { title: string; items: readonly string[]; link: string };
};

const PRICING_ES: PricingContent = {
  page: {
    title: "Precios claros para que tu agenda crezca",
    lead: "Planes mensuales de gestión de Instagram, un pack para empezar con buena base y servicios sueltos. Sabés qué incluye cada uno antes de empezar.",
    plansTitle: "Planes de gestión de Instagram",
    tableService: "Servicio",
    tablePrice: "Precio",
  },
  currency: "EUR",
  taxNote: PRICES_INCLUDE_VAT
    ? "Precios en euros, IVA incluido."
    : "Precios en euros para autónomos y empresas. IVA no incluido.",
  perMonth: "/ mes",
  oneOff: "pago único",
  from: "desde",
  featuredLabel: "Recomendado",
  networkNote: "Todos los planes mensuales incluyen la gestión de una red social.",
  packs: [
    {
      id: "bienvenida",
      name: "Bienvenida",
      goal: "Construir la base",
      price: 130,
      cadence: "once",
      idealFor:
        "Ideal si estás empezando, tenés una cuenta sin estrategia o querés profesionalizar tu perfil antes de comunicar.",
      includes: [
        "Análisis FODA de tu marca",
        "Puntos de dolor de tus clientes",
        "Investigación de tendencias de tu sector",
        "Definición de tu cliente ideal",
        "Tono y estilo de comunicación",
        "Estrategia visual del feed",
        "Optimización SEO del perfil y biografía",
        "Diseño de historias destacadas y portada",
      ],
      outcomes: ["Cliente ideal", "Identidad visual", "Tono de voz", "Objetivos", "Pilares de contenido"],
      note: "Se realiza durante la primera semana. No incluye diseño ni publicaciones en el feed o historias.",
    },
    {
      id: "esencia",
      name: "Esencia",
      goal: "Mantener presencia",
      price: 249,
      cadence: "month",
      idealFor:
        "Ideal si ya tenés una base y querés mantener tu cuenta activa, ordenada y profesional, sin complicaciones.",
      includes: [
        "6 publicaciones al mes (hasta 2 reels)",
        "Historias estratégicas 2 veces por semana",
        "Calendario de contenidos",
        "Copywriting estratégico",
      ],
      outcomes: [
        "Una imagen activa y profesional",
        "Una presencia constante",
        "Comunicación regular de tus servicios y novedades",
        "Contenido pensado con intención",
      ],
    },
    {
      id: "crecimiento",
      name: "Crecimiento",
      goal: "Crecer y posicionarse",
      price: 329,
      cadence: "month",
      featured: true,
      idealFor:
        "Ideal para negocios que quieren dejar de publicar sin rumbo y empezar a trabajar Instagram de forma estratégica.",
      includes: [
        "8 publicaciones al mes (hasta 3 reels)",
        "Historias estratégicas 3 veces por semana",
        "Calendario de contenidos",
        "Copywriting estratégico",
        "Reporte de métricas mensual",
      ],
      outcomes: [
        "Más alcance y visibilidad",
        "Contenido más completo y variado",
        "Reels y formatos de alto impacto",
        "Gestión activa de la comunidad",
        "Optimización y pruebas continuas",
      ],
    },
    {
      id: "evolucion",
      name: "Evolución",
      goal: "Acelerar resultados",
      price: 449,
      cadence: "month",
      idealFor:
        "Ideal para negocios que quieren darle a Instagram un papel más importante dentro de su estrategia comercial.",
      includes: [
        "12 publicaciones al mes (hasta 4 reels)",
        "Historias estratégicas 4 veces por semana",
        "Calendario de contenidos",
        "Copywriting estratégico",
        "Reporte de métricas mensual",
        "Respuesta a comentarios e interacción con tu comunidad",
      ],
      outcomes: [
        "Estrategia integral y personalizada",
        "Máximo volumen de contenido",
        "Reels y formatos avanzados",
        "Gestión completa de la comunidad",
        "Análisis avanzado y optimización continua",
      ],
    },
  ],
  firstStep: {
    title: "El primer paso es gratis: tu auditoría",
    body: "Antes de elegir, pedí tu Auditoría Estratégica gratuita por WhatsApp. Analizo tu perfil y tu negocio y te digo qué plan tiene sentido para vos (o si todavía no necesitás ninguno).",
  },
  custom: {
    title: "Plan a medida",
    body: "Si tu negocio necesita algo fuera de estos planes, diseñamos una propuesta que se adapta a tus objetivos y a tu inversión.",
    factors: [
      "Cantidad y tipo de publicaciones",
      "Plataformas gestionadas",
      "Profundidad del servicio",
    ],
    note: "A menor volumen, menor alcance de resultados: te lo diré con honestidad antes de empezar.",
  },
  services: {
    title: "Servicios principales",
    intro: "Servicios de pago único para dar un paso concreto, con o sin plan mensual.",
    cta: "Ver el servicio",
    items: [
      {
        id: "asesoria",
        title: "Asesoría Estratégica de Instagram",
        desc: "Aprendé a gestionar el Instagram de tu negocio con una asesoría en video personalizada, el Kit de Implementación Lucentia y 7 días de soporte por WhatsApp.",
        price: 197,
        cadence: "once",
        href: "/asesoria-instagram",
      },
      {
        id: "web",
        title: "Página Web Estratégica",
        desc: "Una web que no solo se ve bien: convierte visitas en clientes y reservas, optimizada para móviles y para Google.",
        price: 550,
        from: true,
        cadence: "once",
        href: "/pagina-web",
      },
      {
        id: "whatsapp",
        title: "WhatsApp Business Pro",
        desc: "Transformamos tu WhatsApp Business en una herramienta profesional para atender consultas y convertirlas en reservas.",
        price: 249,
        cadence: "once",
      },
      {
        id: "google",
        title: "Optimización de Google Business Profile",
        desc: "Optimizamos tu ficha de Google para que tu negocio destaque en las búsquedas locales y en Google Maps.",
        price: 149,
        cadence: "once",
        extra: "Actualizaciones mensuales opcionales: 99 €/mes",
      },
    ],
  },
  rateCard: {
    title: "Servicios sueltos",
    intro: "Para trabajos puntuales fuera de los planes mensuales.",
    note: "Precios de referencia; el importe final depende del encargo.",
    items: [
      { service: "Reel básico", detail: "Edición sencilla y música", min: 25 },
      { service: "Reel profesional", detail: "Edición avanzada, copy y llamada a la acción", min: 40, max: 60 },
      { service: "Post individual", detail: "Diseño de imagen y copywriting", min: 15, max: 25 },
      { service: "Carrusel", detail: "De 5 a 7 diapositivas y copy", min: 25, max: 40 },
      { service: "Historia individual", detail: "Diseño de una historia", min: 5, max: 10 },
      { service: "Pack de 5 historias", detail: "Narrativa visual coherente", min: 20, max: 35 },
      { service: "Calendario mensual", detail: "Planificación, fechas clave, pilares, objetivos y copywriting (con o sin diseño)", min: 60, max: 120 },
      { service: "Optimización de perfil", detail: "Bio clara, foto, destacadas estratégicas, SEO y link en bio", min: 25, max: 40 },
    ],
  },
  addOns: {
    title: "Servicios adicionales",
    intro: "Complementá tu plan o contratá solo lo que necesitás.",
    items: [
      {
        title: "Diseño gráfico y branding",
        desc: "Identidad visual completa: logotipo, paleta, tipografías y piezas especiales para que tu marca destaque.",
        by: "Con una diseñadora gráfica profesional",
      },
      {
        title: "Diseño y desarrollo web",
        desc: "Webs institucionales, reservas online, aplicaciones y e-commerce a medida, rápidas y optimizadas.",
        by: "Con Federico, desarrollador",
      },
      {
        title: "Asesoría estratégica personalizada",
        desc: "Una clase grabada y adaptada a tu negocio para aprender a gestionar tu Instagram a tu ritmo.",
        by: "Con Jenifer",
      },
    ],
  },
  notIncluded: {
    title: "Qué no incluyen los planes",
    items: [
      "Publicidad pagada (Meta Ads) ni su inversión",
      "Producción de fotos o vídeos",
      "Atención al cliente, gestión de agenda o venta directa (disponible como servicio adicional)",
      "Trabajo presencial (se presupuesta aparte)",
    ],
  },
  conditions: {
    title: "Cómo trabajamos",
    items: [
      "Permanencia mínima de 3 meses: es el tiempo que necesita una estrategia para dar resultados.",
      "Cancelación con 1 mes de aviso.",
      "Pago por adelantado de cada mes, por Bizum, PayPal o efectivo.",
      "Revisás el contenido en 72 h, con hasta 2 rondas de cambios por pieza.",
      "Atención de lunes a viernes, de 09:00 a 18:00.",
    ],
    link: "Leer las condiciones completas",
  },
};

const PRICING_EN: PricingContent = {
  page: {
    title: "Clear prices to help your diary grow",
    lead: "Monthly Instagram management plans, a pack to start on solid ground, and one-off services. You know what each one includes before you begin.",
    plansTitle: "Instagram management plans",
    tableService: "Service",
    tablePrice: "Price",
  },
  currency: "EUR",
  taxNote: PRICES_INCLUDE_VAT
    ? "Prices in euros, VAT included."
    : "Prices in euros for freelancers and businesses. VAT not included.",
  perMonth: "/ month",
  oneOff: "one-off",
  from: "from",
  featuredLabel: "Recommended",
  networkNote: "Every monthly plan includes management of one social network.",
  packs: [
    {
      id: "bienvenida",
      name: "Welcome",
      goal: "Build the foundation",
      price: 130,
      cadence: "once",
      idealFor:
        "Ideal if you're starting out, have an account with no strategy, or want to professionalise your profile before you start posting.",
      includes: [
        "SWOT analysis of your brand",
        "Your clients' pain points",
        "Trend research in your sector",
        "Ideal client definition",
        "Tone and communication style",
        "Visual feed strategy",
        "Profile SEO and bio optimisation",
        "Highlight covers and profile design",
      ],
      outcomes: ["Ideal client", "Visual identity", "Tone of voice", "Goals", "Content pillars"],
      note: "Delivered in the first week. Does not include post or story design or publishing.",
    },
    {
      id: "esencia",
      name: "Essence",
      goal: "Keep a steady presence",
      price: 249,
      cadence: "month",
      idealFor:
        "Ideal if you already have a foundation and want an active, organised, professional account without the hassle.",
      includes: [
        "6 posts a month (up to 2 reels)",
        "Strategic stories twice a week",
        "Content calendar",
        "Strategic copywriting",
      ],
      outcomes: [
        "An active, professional image",
        "A consistent presence",
        "Regular updates on your services and news",
        "Content created with intent",
      ],
    },
    {
      id: "crecimiento",
      name: "Growth",
      goal: "Grow and position yourself",
      price: 329,
      cadence: "month",
      featured: true,
      idealFor:
        "Ideal for businesses that want to stop posting aimlessly and start using Instagram strategically.",
      includes: [
        "8 posts a month (up to 3 reels)",
        "Strategic stories three times a week",
        "Content calendar",
        "Strategic copywriting",
        "Monthly metrics report",
      ],
      outcomes: [
        "More reach and visibility",
        "Richer, more varied content",
        "High-impact reels and formats",
        "Active community management",
        "Continuous optimisation and testing",
      ],
    },
    {
      id: "evolucion",
      name: "Evolution",
      goal: "Accelerate results",
      price: 449,
      cadence: "month",
      idealFor:
        "Ideal for businesses that want Instagram to play a bigger role in their sales strategy.",
      includes: [
        "12 posts a month (up to 4 reels)",
        "Strategic stories four times a week",
        "Content calendar",
        "Strategic copywriting",
        "Monthly metrics report",
        "Comment replies and community interaction",
      ],
      outcomes: [
        "A complete, personalised strategy",
        "Maximum content volume",
        "Advanced reels and formats",
        "Full community management",
        "Advanced analysis and continuous optimisation",
      ],
    },
  ],
  firstStep: {
    title: "The first step is free: your audit",
    body: "Before choosing, request your free Strategic Audit on WhatsApp. I'll review your profile and your business and tell you which plan makes sense for you (or whether you don't need one yet).",
  },
  custom: {
    title: "Custom plan",
    body: "If your business needs something outside these plans, we design a proposal around your goals and your budget.",
    factors: ["Number and type of posts", "Platforms managed", "Depth of service"],
    note: "Less volume means less reach: I'll be honest about that before we start.",
  },
  services: {
    title: "Core services",
    intro: "One-off services to take a concrete step, with or without a monthly plan.",
    cta: "See the service",
    items: [
      {
        id: "asesoria",
        title: "Strategic Instagram Advisory",
        desc: "Learn to run your business's Instagram with a personalised video session, the Lucentia Implementation Kit and 7 days of WhatsApp support.",
        price: 197,
        cadence: "once",
        href: "/asesoria-instagram",
      },
      {
        id: "web",
        title: "Strategic Website",
        desc: "A website that doesn't just look good: it turns visits into clients and bookings, optimised for mobile and for Google.",
        price: 550,
        from: true,
        cadence: "once",
        href: "/pagina-web",
      },
      {
        id: "whatsapp",
        title: "WhatsApp Business Pro",
        desc: "We turn your WhatsApp Business into a professional tool to answer enquiries and turn them into bookings.",
        price: 249,
        cadence: "once",
      },
      {
        id: "google",
        title: "Google Business Profile optimisation",
        desc: "We optimise your Google listing so your business stands out in local searches and on Google Maps.",
        price: 149,
        cadence: "once",
        extra: "Optional monthly updates: €99/month",
      },
    ],
  },
  rateCard: {
    title: "One-off services",
    intro: "For individual jobs outside the monthly plans.",
    note: "Reference prices; the final amount depends on the brief.",
    items: [
      { service: "Basic reel", detail: "Simple edit and music", min: 25 },
      { service: "Professional reel", detail: "Advanced edit, copy and call to action", min: 40, max: 60 },
      { service: "Single post", detail: "Image design and copywriting", min: 15, max: 25 },
      { service: "Carousel", detail: "5 to 7 slides and copy", min: 25, max: 40 },
      { service: "Single story", detail: "Design of one story", min: 5, max: 10 },
      { service: "5-story pack", detail: "A coherent visual narrative", min: 20, max: 35 },
      { service: "Monthly calendar", detail: "Planning, key dates, pillars, goals and copywriting (with or without design)", min: 60, max: 120 },
      { service: "Profile optimisation", detail: "Clear bio, photo, strategic highlights, SEO and link in bio", min: 25, max: 40 },
    ],
  },
  addOns: {
    title: "Add-on services",
    intro: "Complement your plan or book only what you need.",
    items: [
      {
        title: "Graphic design and branding",
        desc: "Complete visual identity: logo, palette, typefaces and special pieces so your brand stands out.",
        by: "With a professional graphic designer",
      },
      {
        title: "Web design and development",
        desc: "Business websites, online booking, apps and custom e-commerce, fast and optimised.",
        by: "With Federico, developer",
      },
      {
        title: "Personal strategy session",
        desc: "A recorded class tailored to your business so you can learn to run your Instagram at your own pace.",
        by: "With Jenifer",
      },
    ],
  },
  notIncluded: {
    title: "What the plans don't include",
    items: [
      "Paid advertising (Meta Ads) or ad spend",
      "Photo or video production",
      "Customer service, diary management or direct sales (available as an add-on)",
      "On-site work (quoted separately)",
    ],
  },
  conditions: {
    title: "How we work",
    items: [
      "3-month minimum: the time a strategy needs to deliver results.",
      "Cancel with 1 month's notice.",
      "Each month is paid in advance, by Bizum, PayPal or cash.",
      "You review content within 72 h, with up to 2 rounds of changes per piece.",
      "Support Monday to Friday, 09:00 to 18:00.",
    ],
    link: "Read the full terms",
  },
};

export function getPricing(locale: AppLocale): PricingContent {
  return locale === "en" ? PRICING_EN : PRICING_ES;
}

export function usePricing(): PricingContent {
  const { i18n } = useTranslation();
  const locale = (i18n.language === "en" ? "en" : "es") as AppLocale;
  return useMemo(() => getPricing(locale), [locale]);
}

export function formatEuro(value: number, locale: AppLocale): string {
  return new Intl.NumberFormat(locale === "en" ? "en-IE" : "es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatRate(item: RateItem, locale: AppLocale): string {
  if (item.max === undefined) return formatEuro(item.min, locale);
  return `${formatEuro(item.min, locale)} – ${formatEuro(item.max, locale)}`;
}
